import { useEffect, useRef, useState } from 'react';
import Modal from '../../../../common/_custom/Modal/Modal';
import Button from '../../../../common/_custom/Button/Button';
import styles from './RenameReportDialog.module.scss';

interface RenameReportDialogProps {
    open: boolean;
    name: string;
    description: string;
    onClose: () => void;
    onSave: (name: string, description: string) => void;
}

const RenameReportDialog: React.FC<RenameReportDialogProps> = ({ open, name, description, onClose, onSave }) => {
    const [nameDraft, setNameDraft] = useState(name ?? '');
    const [descriptionDraft, setDescriptionDraft] = useState(description ?? '');

    // Only reseed drafts on the closed→open transition. A periodic autosave tick
    // in ReportEditorRoute.persist can call setName/setDescription while the
    // dialog is open; resyncing on every name/description change would clobber
    // the user's unsaved keystrokes.
    const prevOpenRef = useRef(false);
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setNameDraft(name ?? '');
            setDescriptionDraft(description ?? '');
        }
        prevOpenRef.current = open;
    }, [open, name, description]);

    const handleUpdate = () => {
        const trimmed = nameDraft.trim();
        if (!trimmed) return;
        onSave(trimmed, descriptionDraft.trim());
    };

    return (
        <Modal open={open} onClose={onClose} size="flexible" className={styles.modalOverDrawer} withoutCloseButton>
            <div className={styles.dialog}>
                <div className={styles.fields}>
                    <div className={styles.field}>
                        <span className={styles.label}>Report Name</span>
                        <input
                            autoFocus
                            className={styles.nameInput}
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            placeholder="Enter report name"
                            aria-label="Report name"
                        />
                    </div>
                    <div className={styles.field}>
                        <span className={styles.label}>Description</span>
                        <textarea
                            className={styles.descriptionInput}
                            value={descriptionDraft}
                            onChange={(e) => setDescriptionDraft(e.target.value)}
                            placeholder="Add your description here"
                            aria-label="Report description"
                        />
                    </div>
                </div>
                <div className={styles.actions}>
                    <Button
                        text="Cancel"
                        variant="purpleinverted"
                        onClick={onClose}
                        className={styles.actionBtn}
                        style={{ marginTop: 0 }}
                    />
                    <Button
                        text="Update"
                        variant="primary"
                        onClick={handleUpdate}
                        disabled={!nameDraft.trim()}
                        className={styles.actionBtn}
                        style={{ marginTop: 0 }}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default RenameReportDialog;
